import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from './Navbar';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  imageUrl?: string;
  schema?: any;
}

/** WhatsApp / Facebook crawlers need absolute JPG URLs in static HTML + Helmet */
export const DEFAULT_OG_IMAGE = 'https://www.barkinsayin.com/og-image.jpg';

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  imageUrl = DEFAULT_OG_IMAGE,
  schema,
}) => {
  const { language } = useLanguage();
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname === `/${language}`;

  // Default meta values
  const defaultMeta = {
    en: {
      title: 'Barkin Sayin – Global Fintech Entrepreneur & Smart TV Innovator',
      description: 'Discover Barkin Sayin, entrepreneur and investor behind Trade on TV and global fintech innovations. Leading smart TV applications and digital transformation across Germany, UAE, and Turkey.',
      keywords: [
        'Barkin Sayin',
        'Fintech Entrepreneur',
        'Trade on TV',
        'Smart TV Investment App',
        'Financial Technology Expert',
        'Global Business Consultant',
        'Dubai Tech Entrepreneur',
        'Digital Innovation Leader',
        'Investor from Germany UAE Turkey'
      ],
      heroTitle: 'Barkin Sayin',
      heroSubtitle: 'Entrepreneur | Investor | Technology Leader',
      homeDescription: 'Barkin Sayin is a fintech entrepreneur, global investor, and the founder of Trade on TV – the first smart TV application for financial markets. With a focus on digital transformation, smart technologies, and international business, he builds the future from Germany, Turkey, and the UAE.'
    },
    tr: {
      title: 'Barkın Sayın – Fintech Girişimcisi ve Akıllı TV Yenilikçisi',
      description: 'Trade on TV\'nin kurucusu Barkın Sayın ile tanışın: Fintech girişimcisi, yatırımcı ve dijital dönüşüm lideri. Almanya, BAE ve Türkiye\'de teknolojiyle geleceği inşa ediyor.',
      keywords: [
        'Barkın Sayın kimdir',
        'Fintech girişimcisi',
        'Akıllı TV yatırım uygulaması',
        'Trade on TV nedir',
        'Finans teknolojileri lideri',
        'Uluslararası yatırımcı',
        'Teknoloji danışmanı Türkiye Dubai',
        'Dijital dönüşüm uzmanı'
      ],
      heroTitle: 'Barkın Sayın',
      heroSubtitle: 'Girişimci | Yatırımcı | Teknoloji Lideri',
      homeDescription: 'Barkın Sayın, Trade on TV adlı finans odaklı ilk akıllı TV uygulamasının kurucusu, fintech girişimcisi ve global yatırımcıdır. Dijital dönüşüm, akıllı teknolojiler ve uluslararası iş stratejileriyle Almanya, Türkiye ve BAE\'de geleceği şekillendiriyor.'
    },
    // Fallback for other languages
    default: {
      title: 'Barkin Sayin – Global Fintech Entrepreneur & Smart TV Innovator',
      description: 'Discover Barkin Sayin, entrepreneur and investor behind Trade on TV and global fintech innovations. Leading smart TV applications and digital transformation across Germany, UAE, and Turkey.',
      keywords: [
        'Barkin Sayin',
        'Fintech Entrepreneur',
        'Trade on TV',
        'Smart TV Investment App',
        'Financial Technology Expert',
        'Global Business Consultant',
        'Dubai Tech Entrepreneur'
      ],
      heroTitle: 'Barkin Sayin',
      heroSubtitle: 'Entrepreneur | Investor | Technology Leader',
      homeDescription: 'Barkin Sayin is a fintech entrepreneur, global investor, and the founder of Trade on TV – the first smart TV application for financial markets. With a focus on digital transformation, smart technologies, and international business, he builds the future from Germany, Turkey, and the UAE.'
    }
  };

  // Get language-specific metadata, falling back to English if language not found
  const metaForLanguage = defaultMeta[language as keyof typeof defaultMeta] || defaultMeta.default;

  // Use provided values or defaults
  const pageTitle = title || metaForLanguage.title;
  const pageDescription = description || metaForLanguage.description;
  const pageKeywords = keywords || metaForLanguage.keywords;

  // Canonical URL
  const baseUrl = 'https://www.barkinsayin.com';
  const canonicalUrl = `${baseUrl}${location.pathname}`;

  // Generate appropriate language alternate links
  const generateAlternateLinks = () => {
    const paths = {
      en: location.pathname.replace(/^\/(en|tr|fr|es|ar)/, '') || '/',
      tr: location.pathname.replace(/^\/(en|tr|fr|es|ar)/, '/tr') || '/tr',
      fr: location.pathname.replace(/^\/(en|tr|fr|es|ar)/, '/fr') || '/fr',
      es: location.pathname.replace(/^\/(en|tr|fr|es|ar)/, '/es') || '/es',
      ar: location.pathname.replace(/^\/(en|tr|fr|es|ar)/, '/ar') || '/ar'
    };

    return Object.entries(paths).map(([lang, path]) => {
      const hrefLang = lang === 'en' ? 'en-US' : 
                      lang === 'tr' ? 'tr-TR' : 
                      lang === 'fr' ? 'fr-FR' : 
                      lang === 'es' ? 'es-ES' : 
                      lang === 'ar' ? 'ar-AE' : 'x-default';
      return (
        <link 
          key={lang} 
          rel="alternate" 
          hrefLang={hrefLang} 
          href={`${baseUrl}${path}`} 
        />
      );
    });
  };

  // Default schema for home page
  const defaultSchema = isHomePage ? {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": language === 'tr' ? "Barkın Sayın" : "Barkin Sayin",
    "url": "https://www.barkinsayin.com",
    "image": DEFAULT_OG_IMAGE,
    "sameAs": [
      "https://www.linkedin.com/in/barkinsayin",
      "https://twitter.com/barkinsayin"
    ],
    "jobTitle": language === 'tr' ? "Girişimci ve Yatırımcı" : "Entrepreneur and Investor",
    "worksFor": {
      "@type": "Organization",
      "name": "Trade on TV"
    },
    "description": pageDescription
  } : null;

  // Website schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": language === 'tr' ? "Barkın Sayın Resmi Web Sitesi" : "Barkin Sayin Official Website",
    "url": "https://www.barkinsayin.com",
    "description": pageDescription,
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.barkinsayin.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <html lang={language} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords.join(', ')} />
      <meta name="author" content={language === 'tr' ? "Barkın Sayın" : "Barkin Sayin"} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Language alternates */}
      {generateAlternateLinks()}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta itemProp="image" content={imageUrl} />
      <meta property="og:image:alt" content={language === 'tr' ? "Barkın Sayın" : "Barkin Sayin"} />
      <meta property="og:site_name" content={language === 'tr' ? "Barkın Sayın" : "Barkin Sayin"} />
      <meta property="og:locale" content={language === 'tr' ? "tr_TR" : "en_US"} />
      <meta property="og:locale:alternate" content={language === 'tr' ? "en_US" : "tr_TR"} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={language === 'tr' ? "Barkın Sayın" : "Barkin Sayin"} />
      
      {/* Structured Data */}
      {(schema || defaultSchema) && (
        <script type="application/ld+json">
          {JSON.stringify(schema || defaultSchema)}
        </script>
      )}
      
      {/* Website Schema */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;

// Export Home page metadata constants to use in the Home component
export const getHomeMetadata = (language: string) => {
  const meta = {
    en: {
      heroTitle: 'Barkin Sayin',
      heroSubtitle: 'Entrepreneur | Investor | Technology Leader',
      homeDescription: 'Barkin Sayin is a fintech entrepreneur, global investor, and the founder of Trade on TV – the first smart TV application for financial markets. With a focus on digital transformation, smart technologies, and international business, he builds the future from Germany, Turkey, and the UAE.'
    },
    tr: {
      heroTitle: 'Barkın Sayın',
      heroSubtitle: 'Girişimci | Yatırımcı | Teknoloji Lideri',
      homeDescription: 'Barkın Sayın, Trade on TV adlı finans odaklı ilk akıllı TV uygulamasının kurucusu, fintech girişimcisi ve global yatırımcıdır. Dijital dönüşüm, akıllı teknolojiler ve uluslararası iş stratejileriyle Almanya, Türkiye ve BAE\'de geleceği şekillendiriyor.'
    }
  };
  
  return meta[language as keyof typeof meta] || meta.en;
}; 